import { CollapsibleContent, Button } from '@schibsted-svp/react-ui';
import { relative as getRelativeDate } from 'lib/date';
import { capitalize } from 'lib/string';
import { getChangeFieldName } from './helpers';
import type { CategoryHistoryData, CategoryHistoryEntryAction } from './types';
import css from './HistoryListItem.module.scss';

type HistoryListItemProps = {
    data: CategoryHistoryData;
    openPreview: (data: CategoryHistoryData) => void;
};

// an entry only ever has zero changes when it's the oldest one (see prepareCategoryHistoryData) -
// its action tells us whether that's because it's the true creation record or because history
// tracking started later and this is just the earliest record we happen to have
const NO_CHANGES_TEXT: Record<CategoryHistoryEntryAction, string> = {
    created: 'Category created',
    updated: 'First recorded entry - no previous version available to identify changed fields',
};

export function HistoryListItem({ data, openPreview }: HistoryListItemProps) {
    const { id, date, action, changes } = data;

    return (
        <li className={css.listItem}>
            <CollapsibleContent
                headerClassName={css.header}
                arrowIconClassName={css.headerArrow}
                customHeader={
                    <>
                        <div className={css.headerDate}>{date ? getRelativeDate(date * 1000) : 'Unknown date'}</div>
                        <span>{capitalize(action)}</span>
                    </>
                }
            >
                <div className={css.content}>
                    <div className={css.actionButtons}>
                        <Button type="button" onClick={() => openPreview(data)} size="small" variant="primary">
                            Preview
                        </Button>
                    </div>
                    <ul className={css.changesList}>
                        {changes.length ? (
                            changes.map((change) => (
                                <li key={`history:change:${id}:${change.path}`}>
                                    {getChangeFieldName(change.path)} {change.action}
                                </li>
                            ))
                        ) : (
                            <li className={css.noChanges}>{NO_CHANGES_TEXT[action]}</li>
                        )}
                    </ul>
                </div>
            </CollapsibleContent>
        </li>
    );
}
